// resources.actions.js - Redux actions for Resources page
import ResourcesApi from '../../api/resourcesApi.js';
import { RESOURCES_ACTIONS } from '../../reducers/resources.js';

// Fetch all resources and related data
export const fetchResources = () => async dispatch => {
	dispatch({ type: RESOURCES_ACTIONS.FETCH_REQUEST });

	try {
		const response = await ResourcesApi.getResources();
		// API may return data directly or wrapped in response.data.data
		const data = response.data?.data || response.data;
		dispatch({
			type: RESOURCES_ACTIONS.FETCH_SUCCESS,
			payload: data
		});
		return response;
	} catch (error) {
		dispatch({
			type: RESOURCES_ACTIONS.FETCH_FAILURE,
			payload: { error: error.message }
		});
		throw error;
	}
};

// Add new resource
export const addResource = resourceData => async dispatch => {
	dispatch({ type: RESOURCES_ACTIONS.ADD_REQUEST });

	try {
		const response = await ResourcesApi.addResource(resourceData);
		dispatch({
			type: RESOURCES_ACTIONS.ADD_SUCCESS,
			payload: { resource: response.data.data || response.data }
		});
		return response;
	} catch (error) {
		if (error.response?.status !== 400 && error.response?.status !== 422) {
			dispatch({
				type: RESOURCES_ACTIONS.ADD_FAILURE,
				payload: { error: error.message }
			});
		}
		throw error;
	}
};

// Update existing resource
export const updateResource = (resourceId, resourceData) => async dispatch => {
	dispatch({ type: RESOURCES_ACTIONS.UPDATE_REQUEST });

	try {
		const response = await ResourcesApi.updateResource(resourceId, resourceData);
		dispatch({
			type: RESOURCES_ACTIONS.UPDATE_SUCCESS,
			payload: { resource: response.data.data || response.data }
		});
		return response;
	} catch (error) {
		if (error.response?.status !== 400 && error.response?.status !== 422) {
			dispatch({
				type: RESOURCES_ACTIONS.UPDATE_FAILURE,
				payload: { error: error.message }
			});
		}
		throw error;
	}
};

// Toggle resource publication status
export const toggleResourceVisibility = (resourceId, currentPublished) => async dispatch => {
	const newPublished = currentPublished ? 0 : 1;

	// Optimistic update
	dispatch({
		type: RESOURCES_ACTIONS.TOGGLE_VISIBILITY_OPTIMISTIC,
		payload: { resourceId, published: newPublished }
	});

	try {
		const response = await ResourcesApi.togglePublication(resourceId);
		dispatch({
			type: RESOURCES_ACTIONS.TOGGLE_VISIBILITY_SUCCESS,
			payload: { resourceId, published: newPublished }
		});
		return response;
	} catch (error) {
		// Rollback on failure
		dispatch({
			type: RESOURCES_ACTIONS.TOGGLE_VISIBILITY_ROLLBACK,
			payload: { resourceId, published: currentPublished }
		});
		throw error;
	}
};

// Delete resource
export const deleteResource = resourceId => async dispatch => {
	dispatch({ type: RESOURCES_ACTIONS.DELETE_REQUEST });

	try {
		const response = await ResourcesApi.deleteResource(resourceId);
		dispatch({
			type: RESOURCES_ACTIONS.DELETE_SUCCESS,
			payload: { resourceId }
		});
		return response;
	} catch (error) {
		dispatch({
			type: RESOURCES_ACTIONS.DELETE_FAILURE,
			payload: { error: error.message }
		});
		throw error;
	}
};

// Block resource type (prevent scheduling together)
export const blockResourceType =
	(typeId, blockedType, resourcesTypes) => async (dispatch, getState) => {
		// Save previous resourcesTypes for rollback
		const previousResourcesTypes = getState().resources?.resourcesTypes || resourcesTypes;

		// Optimistic update
		dispatch({
			type: RESOURCES_ACTIONS.BLOCK_TYPE_OPTIMISTIC,
			payload: { typeId, blockedType }
		});

		try {
			const response = await ResourcesApi.blockResourceType(typeId, blockedType.id);
			dispatch({
				type: RESOURCES_ACTIONS.BLOCK_TYPE_SUCCESS,
				payload: { typeId, blockedTypeId: blockedType.id }
			});
			return response;
		} catch (error) {
			// Rollback on failure
			dispatch({
				type: RESOURCES_ACTIONS.BLOCK_TYPE_ROLLBACK,
				payload: { typeId, blockedTypeId: blockedType.id, previousResourcesTypes }
			});
			throw error;
		}
	};

// Unblock resource type
export const unblockResourceType =
	(typeId, blockedTypeId, resourcesTypes) => async (dispatch, getState) => {
		// Save previous resourcesTypes for rollback
		const previousResourcesTypes = getState().resources?.resourcesTypes || resourcesTypes;

		// Optimistic update
		dispatch({
			type: RESOURCES_ACTIONS.UNBLOCK_TYPE_OPTIMISTIC,
			payload: { typeId, blockedTypeId }
		});

		try {
			const response = await ResourcesApi.unblockResourceType(typeId, blockedTypeId);
			dispatch({
				type: RESOURCES_ACTIONS.UNBLOCK_TYPE_SUCCESS,
				payload: { typeId, blockedTypeId }
			});
			return response;
		} catch (error) {
			// Rollback on failure
			dispatch({
				type: RESOURCES_ACTIONS.UNBLOCK_TYPE_ROLLBACK,
				payload: { typeId, blockedTypeId, previousResourcesTypes }
			});
			throw error;
		}
	};

// Update resource type name
export const updateResourceTypeName = (typeId, newName, resourcesTypes) => async dispatch => {
	// Find current type to get old name and blockStatus
	const currentType = resourcesTypes.find(t => String(t.id) === String(typeId));
	const oldName = currentType?.name;
	// Convert blockStatus to boolean (API expects bool)
	const blockStatus =
		currentType?.blockStatus === '1' ||
		currentType?.blockStatus === 1 ||
		currentType?.blockStatus === true;

	// Optimistic update
	dispatch({
		type: RESOURCES_ACTIONS.UPDATE_TYPE_NAME_OPTIMISTIC,
		payload: { typeId, newName }
	});

	try {
		const response = await ResourcesApi.updateResourceType(typeId, { name: newName, blockStatus });
		dispatch({
			type: RESOURCES_ACTIONS.UPDATE_TYPE_NAME_SUCCESS,
			payload: { typeId }
		});
		return response;
	} catch (error) {
		// Rollback on failure
		dispatch({
			type: RESOURCES_ACTIONS.UPDATE_TYPE_NAME_ROLLBACK,
			payload: { typeId, oldName }
		});
		throw error;
	}
};

// ============ Maintenance Actions ============

// Fetch maintenance events for a resource
export const fetchMaintenance = resourceId => async dispatch => {
	dispatch({ type: RESOURCES_ACTIONS.FETCH_MAINTENANCE_REQUEST });

	try {
		const response = await ResourcesApi.getMaintenance(resourceId);
		const data = response.data?.data || response.data;
		dispatch({
			type: RESOURCES_ACTIONS.FETCH_MAINTENANCE_SUCCESS,
			payload: {
				resourceId,
				events: data.events || data.maintenanceEvents || [],
				defaultEvent: data.defaultEvent,
				enforceComments: data.enforceComments ?? data.enforseComments
			}
		});
		return response;
	} catch (error) {
		dispatch({
			type: RESOURCES_ACTIONS.FETCH_MAINTENANCE_FAILURE,
			payload: { error: error.message }
		});
		throw error;
	}
};

// Add maintenance event with optimistic update
export const addMaintenance = (resourceId, maintenanceData) => async dispatch => {
	const tempId = `temp_${Date.now()}`;

	// Optimistic update
	dispatch({
		type: RESOURCES_ACTIONS.ADD_MAINTENANCE_OPTIMISTIC,
		payload: { resourceId, tempId, maintenanceEvent: maintenanceData }
	});

	try {
		const response = await ResourcesApi.addMaintenance(resourceId, maintenanceData);
		const newEvent = response.data?.data?.event || response.data?.event || response.data;
		dispatch({
			type: RESOURCES_ACTIONS.ADD_MAINTENANCE_SUCCESS,
			payload: { resourceId, tempId, maintenanceEvent: newEvent }
		});
		return response;
	} catch (error) {
		// Rollback on failure
		dispatch({
			type: RESOURCES_ACTIONS.ADD_MAINTENANCE_ROLLBACK,
			payload: { resourceId, tempId }
		});
		throw error;
	}
};

// Update maintenance event with optimistic update
export const updateMaintenance =
	(resourceId, maintenanceId, maintenanceData, previousEvent) => async dispatch => {
		// Optimistic update
		dispatch({
			type: RESOURCES_ACTIONS.UPDATE_MAINTENANCE_OPTIMISTIC,
			payload: { resourceId, maintenanceId, maintenanceEvent: maintenanceData, previousEvent }
		});

		try {
			const response = await ResourcesApi.updateMaintenance(
				resourceId,
				maintenanceId,
				maintenanceData
			);
			dispatch({
				type: RESOURCES_ACTIONS.UPDATE_MAINTENANCE_SUCCESS,
				payload: { resourceId, maintenanceId }
			});
			return response;
		} catch (error) {
			// Rollback on failure
			dispatch({
				type: RESOURCES_ACTIONS.UPDATE_MAINTENANCE_ROLLBACK,
				payload: { resourceId, maintenanceId }
			});
			throw error;
		}
	};

// Delete maintenance event with optimistic update
export const deleteMaintenance = (resourceId, maintenanceId, previousEvent) => async dispatch => {
	// Optimistic update
	dispatch({
		type: RESOURCES_ACTIONS.DELETE_MAINTENANCE_OPTIMISTIC,
		payload: { resourceId, maintenanceId, previousEvent }
	});

	try {
		const response = await ResourcesApi.deleteMaintenance(resourceId, maintenanceId);
		dispatch({
			type: RESOURCES_ACTIONS.DELETE_MAINTENANCE_SUCCESS,
			payload: { resourceId, maintenanceId }
		});
		return response;
	} catch (error) {
		// Rollback on failure
		dispatch({
			type: RESOURCES_ACTIONS.DELETE_MAINTENANCE_ROLLBACK,
			payload: { resourceId }
		});
		throw error;
	}
};
